import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import api from '../utils/api';
import {
  ChevronRight, User, Users, MapPin, Upload, CheckCircle, FileText,
  Phone, Mail, Briefcase, GraduationCap, Heart, Globe, Calendar, Baby,
  Download, CreditCard, Wallet, PenTool, Shield, AlertCircle, Loader2, UserCheck, Camera, X
} from 'lucide-react';

const STEPS = [
  { id: 1, label: 'Identificacao', icon: User },
  { id: 2, label: 'Filiacao', icon: Users },
  { id: 3, label: 'Trajectoria e Filhos', icon: MapPin },
  { id: 4, label: 'Documentos', icon: Upload },
  { id: 5, label: 'Confirmacao', icon: CheckCircle },
  { id: 6, label: 'Assinatura e RGPD', icon: PenTool },
];

const MARITAL_OPTIONS = ['Solteiro(a)', 'Casado(a)', 'Divorciado(a)', 'Viuvo(a)', 'Uniao de Facto'];

const EDUCATION_OPTIONS = [
  'Ensino Primario',
  'Ensino Secundario (1. Ciclo)',
  'Ensino Secundario (2. Ciclo)',
  'Curso Tecnico-Profissional',
  'Bacharelato',
  'Licenciatura',
  'Pos-Graduacao',
  'Mestrado',
  'Doutoramento',
  'Outro',
];

const ANGOLA_PROVINCES = [
  'Bengo', 'Benguela', 'Bie', 'Cabinda', 'Cuando Cubango',
  'Cuanza Norte', 'Cuanza Sul', 'Cunene', 'Huambo', 'Huila',
  'Luanda', 'Lunda Norte', 'Lunda Sul', 'Malanje', 'Moxico',
  'Namibe', 'Uige', 'Zaire',
];

const INITIAL_CHILDREN = Array.from({ length: 7 }, () => ({ name: '', birth_date: '' }));

export default function ConsularCard() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [issueType, setIssueType] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoValidation, setPhotoValidation] = useState({ error: null });
  const [docsByCategory, setDocsByCategory] = useState({ passport: null, bi: null, residence: null, nie: null, other: [] });
  const [cardResult, setCardResult] = useState(null);
  const [paymentUrl, setPaymentUrl] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [rgpdAccepted, setRgpdAccepted] = useState(false);

  const [form, setForm] = useState({
    full_name: '', birth_date: '', birth_place: '', province: '', municipality: '',
    education: '', profession: '', marital_status: '', workplace: '',
    phone: '', email: '', current_residence: '', address_spain: '', city: '', postal_code: '',
    passport_number: '', nationality: 'Angolana',
    father_name: '', father_birth_date: '', father_residence: '',
    mother_name: '', mother_birth_date: '', mother_residence: '',
    arrival_date_spain: '', travel_reason: '', notes: '',
    previous_card_number: '', previous_card_expiry: '', previous_card_id: '',
  });
  const [children, setChildren] = useState(INITIAL_CHILDREN);

  useEffect(() => {
    if (user) {
      setForm(prev => ({
        ...prev,
        full_name: user.full_name || prev.full_name,
        email: user.email || prev.email,
        phone: user.phone || prev.phone,
        birth_date: user.birth_date || prev.birth_date,
        passport_number: user.passport_number || prev.passport_number,
        address_spain: user.address || prev.address_spain,
        city: user.city || prev.city,
      }));
    }
  }, [user]);

  // Pre-fill from URL params (renewal link)
  useEffect(() => {
    const tipo = searchParams.get('tipo');
    const ref = searchParams.get('ref');
    if (tipo === 'renovacao' && ref) {
      setIssueType('renewal');
      setForm(prev => ({ ...prev, previous_card_number: ref }));
    }
    const payRef = searchParams.get('ref');
    const paymentSuccess = searchParams.get('payment_success');
    if (payRef && paymentSuccess === 'true') {
      checkPaymentStatus(payRef);
    }
  }, [searchParams]);

  // Lookup previous card for renewal pre-fill
  useEffect(() => {
    if (issueType === 'renewal' && form.previous_card_number?.length >= 8) {
      const timer = setTimeout(() => {
        api.get(`/consular-cards/lookup/${form.previous_card_number}`)
          .then(r => {
            if (r.data) {
              setForm(prev => ({
                ...prev,
                full_name: r.data.full_name || prev.full_name,
                birth_date: r.data.birth_date || prev.birth_date,
                birth_place: r.data.birth_place || prev.birth_place,
                municipality: r.data.municipality || prev.municipality,
                province: r.data.province || prev.province,
                education: r.data.education || prev.education,
                profession: r.data.profession || prev.profession,
                marital_status: r.data.marital_status || prev.marital_status,
                workplace: r.data.workplace || prev.workplace,
                phone: r.data.phone || prev.phone,
                address_spain: r.data.address_spain || prev.address_spain,
                email: r.data.email || prev.email,
                passport_number: r.data.passport_number || prev.passport_number,
                father_name: r.data.father_name || prev.father_name,
                mother_name: r.data.mother_name || prev.mother_name,
                arrival_date_spain: r.data.arrival_date_spain || prev.arrival_date_spain,
              }));
              if (r.data.children_parsed?.length > 0) {
                const filled = [...r.data.children_parsed];
                while (filled.length < 7) filled.push({ name: '', birth_date: '' });
                setChildren(filled.slice(0, 7));
              }
              toast.success('Dados pre-preenchidos a partir do cartao anterior.');
            }
          })
          .catch(() => {});
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [form.previous_card_number, issueType]);

  const checkPaymentStatus = async (ref) => {
    setCheckingPayment(true);
    try {
      const res = await api.get(`/consular-cards/${ref}`);
      if (res.data) {
        setCardResult(res.data);
        if (res.data.payment_status === 'paid') {
          setStep(8);
          toast.success('Pagamento confirmado com sucesso!');
        } else {
          setStep(7);
        }
      }
    } catch { toast.error('Erro ao verificar estado do pagamento'); }
    finally { setCheckingPayment(false); }
  };

  const set = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));
  const setChild = (idx, field) => (e) => {
    setChildren(prev => prev.map((c, i) => i === idx ? { ...c, [field]: e.target.value } : c));
  };

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
      setPhotoValidation({ error: 'Formato invalido. Use JPG ou PNG.' }); return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setPhotoValidation({ error: 'Ficheiro demasiado grande. Maximo 5MB.' }); return;
    }
    const img = new Image();
    img.onload = () => {
      if (img.width < 200 || img.height < 250) {
        setPhotoValidation({ error: `Dimensoes insuficientes (${img.width}x${img.height}px). Minimo: 300x400px.` });
      } else {
        setPhotoValidation({ error: null });
        setPhotoFile(file);
        setPhotoPreview(URL.createObjectURL(file));
      }
    };
    img.src = URL.createObjectURL(file);
  };

  const handleDocUpload = (category) => (e) => {
    const f = e.target.files?.[0];
    if (f && f.size <= 10 * 1024 * 1024) {
      setDocsByCategory(p => ({ ...p, [category]: f }));
    } else if (f) {
      toast.error('Ficheiro demasiado grande. Maximo 10MB.');
    }
  };

  const handleOtherDocs = (e) => {
    const files = Array.from(e.target.files || []);
    setDocsByCategory(p => ({ ...p, other: [...p.other, ...files] }));
  };

  // Signature canvas
  useEffect(() => {
    if (step === 6 && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [step]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = ((e.clientX || e.touches?.[0]?.clientX) - rect.left) * scaleX;
    const y = ((e.clientY || e.touches?.[0]?.clientY) - rect.top) * scaleY;
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = ((e.clientX || e.touches?.[0]?.clientX) - rect.left) * scaleX;
    const y = ((e.clientY || e.touches?.[0]?.clientY) - rect.top) * scaleY;
    const ctx = canvas.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing && canvasRef.current) setSignatureData(canvasRef.current.toDataURL('image/png'));
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setSignatureData(null);
  };

  const canNext = () => {
    if (step === 1) return issueType && form.full_name && form.birth_date && form.birth_place && form.municipality && form.province && form.phone && form.address_spain && form.email && form.passport_number;
    if (step === 2) return form.father_name && form.mother_name;
    if (step === 3) return true;
    if (step === 4) return photoFile && !photoValidation.error && (docsByCategory.passport || docsByCategory.bi) && docsByCategory.residence;
    if (step === 5) return true;
    if (step === 6) return signatureData && rgpdAccepted;
    return true;
  };

  const handleSubmit = async () => {
    if (!signatureData || !rgpdAccepted) { toast.error('Por favor assine o formulario e aceite os termos RGPD'); return; }
    setSubmitting(true);
    try {
      // Upload photo
      let photoUrl = null;
      if (photoFile) {
        const fd = new FormData();
        fd.append('file', photoFile);
        fd.append('category', 'photo');
        try {
          const upRes = await api.post('/consular-cards/upload-document', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          photoUrl = upRes.data?.file_path || null;
        } catch { /* continue */ }
      }

      // Upload categorized docs
      let docPassport = null, docBi = null, docResidence = null, docNie = null;
      const uploadDoc = async (file, cat) => {
        if (!file) return null;
        const fd = new FormData();
        fd.append('file', file);
        fd.append('category', cat);
        try {
          const r = await api.post('/consular-cards/upload-document', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          return r.data?.file_path || null;
        } catch { return null; }
      };
      docPassport = await uploadDoc(docsByCategory.passport, 'passport');
      docBi = await uploadDoc(docsByCategory.bi, 'bi');
      docResidence = await uploadDoc(docsByCategory.residence, 'residence');
      docNie = await uploadDoc(docsByCategory.nie, 'nie');

      // Upload other docs
      const otherDocs = [];
      for (const file of docsByCategory.other) {
        const path = await uploadDoc(file, 'other');
        if (path) otherDocs.push({ name: file.name, path });
      }

      // Filter children (only non-empty)
      const filledChildren = children.filter(c => c.name.trim());

      const payload = {
        ...form,
        issue_type: issueType,
        card_type: 'cartao_consular',
        children: filledChildren.length > 0 ? JSON.stringify(filledChildren) : null,
        photo_url: photoUrl,
        doc_passport: docPassport,
        doc_bi: docBi,
        doc_residence: docResidence,
        doc_nie: docNie,
        documents: otherDocs.length > 0 ? otherDocs : null,
        signature_data: signatureData,
        rgpd_accepted: true,
        rgpd_accepted_at: new Date().toISOString(),
      };

      const res = await api.post('/consular-cards', payload);
      setCardResult(res.data);
      if (res.data.payment_url) {
        toast.success('Pedido registado! A redirecionar para pagamento...');
        setPaymentUrl(res.data.payment_url);
      } else {
        toast.success(`Pedido registado! Referencia: ${res.data.reference_code}`);
      }
      setStep(7);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao submeter o pedido');
    } finally { setSubmitting(false); }
  };

  const proceedToPayment = async () => {
    if (!cardResult?.id) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/consular-cards/${cardResult.id}/payment`);
      if (res.data.url) window.location.href = res.data.url;
      else toast.error('Erro ao criar sessao de pagamento');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao iniciar pagamento');
    } finally { setSubmitting(false); }
  };

  const downloadInvoice = () => {
    if (cardResult?.id) window.open(`${process.env.REACT_APP_BACKEND_URL}/api/consular-cards/${cardResult.id}/download-invoice`, '_blank');
  };

  const inputCls = "w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#CC0000]/30 focus:border-[#CC0000] outline-none transition-all";
  const labelCls = "block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide";

  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#CC0000] mx-auto mb-4" />
          <p className="text-gray-600">A verificar estado do pagamento...</p>
        </div>
      </div>
    );
  }

  const DOC_CATEGORIES = [
    { id: 'passport', label: 'Passaporte Angolano', sublabel: issueType === 'renewal' ? 'Passaporte actual (obrigatorio para renovacao)' : 'Copia das paginas com foto e dados pessoais', required: true, icon: FileText },
    { id: 'bi', label: 'Bilhete de Identidade (BI)', sublabel: 'Frente e verso do BI angolano (alternativa ao passaporte)', required: false, icon: CreditCard },
    { id: 'residence', label: 'Comprovativo de Residencia em Espanha', sublabel: 'Certificado de empadronamiento, contrato ou factura recente', required: true, icon: MapPin },
    { id: 'nie', label: 'NIE / Autorizacao de Residencia', sublabel: 'Numero de Identificacao de Estrangeiro (se disponivel)', required: false, icon: Globe },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#0A0A0A] text-white">
        <div className="max-w-4xl mx-auto px-4 py-10">
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
            <Link to="/" className="hover:text-white">{t('nav.home')}</Link>
            <ChevronRight size={12} />
            <span className="text-white">Inscricao Consular</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold" data-testid="consular-card-title">Pedido de Cartao Consular</h1>
          <p className="text-gray-400 text-sm mt-1">Formulario de Inscricao Consular - Embaixada de Angola em Espanha</p>
          <p className="text-[#FFD700] text-sm font-semibold mt-2">Taxa: EUR 5,00</p>
        </div>
      </div>

      {/* Progress Steps */}
      {step <= 6 && (
        <div className="bg-white border-b sticky top-0 z-20">
          <div className="max-w-4xl mx-auto px-4 py-3">
            <div className="flex gap-1">
              {STEPS.map((s) => {
                const Icon = s.icon;
                const isActive = step === s.id;
                const isDone = step > s.id;
                return (
                  <div key={s.id} className={`flex-1 flex items-center gap-2 px-2 py-2 rounded-lg text-xs font-medium transition-all ${
                    isActive ? 'bg-[#CC0000] text-white' : isDone ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-400'
                  }`}>
                    {isDone ? <CheckCircle size={14} /> : <Icon size={14} />}
                    <span className="hidden md:inline truncate">{s.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Step 1: Identificacao */}
        {step === 1 && (
          <div className="bg-white rounded-xl border p-6 space-y-5" data-testid="step-identification">
            <div className="flex items-center gap-3 mb-2">
              <User size={24} className="text-[#CC0000]" />
              <div>
                <h2 className="text-lg font-bold text-gray-900">Identificacao</h2>
                <p className="text-xs text-gray-500">Dados pessoais conforme documento de identificacao</p>
              </div>
            </div>

            {/* Issue Type Selector */}
            <div className="mb-6" data-testid="issue-type-selector">
              <label className="block text-sm font-bold text-gray-700 mb-3">
                Tipo de Pedido <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  issueType === 'new' ? 'border-[#CC0000] bg-red-50 text-[#CC0000]' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                }`}>
                  <input type="radio" name="issueType" value="new" checked={issueType === 'new'} onChange={() => setIssueType('new')} className="accent-[#CC0000]" data-testid="radio-new-issue" />
                  <div>
                    <p className="font-semibold text-sm">Nova Emissao</p>
                    <p className="text-xs opacity-70">Primeiro cartao consular</p>
                  </div>
                </label>
                <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  issueType === 'renewal' ? 'border-[#CC0000] bg-red-50 text-[#CC0000]' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                }`}>
                  <input type="radio" name="issueType" value="renewal" checked={issueType === 'renewal'} onChange={() => setIssueType('renewal')} className="accent-[#CC0000]" data-testid="radio-renewal" />
                  <div>
                    <p className="font-semibold text-sm">Renovacao</p>
                    <p className="text-xs opacity-70">Ja possuo cartao anterior</p>
                  </div>
                </label>
              </div>

              {issueType === 'renewal' && (
                <div className="mt-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <p className="text-sm font-semibold text-amber-800 mb-2">Renovacao - Informacao adicional necessaria</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className={labelCls}>N. do Cartao Anterior</label>
                      <input className={inputCls} value={form.previous_card_number} onChange={set('previous_card_number')} placeholder="ex: CC-260042" data-testid="input-previous-card" />
                    </div>
                    <div>
                      <label className={labelCls}>Data de Caducidade do Cartao Anterior</label>
                      <input type="date" className={inputCls} value={form.previous_card_expiry} onChange={set('previous_card_expiry')} data-testid="input-previous-expiry" />
                    </div>
                  </div>
                  <p className="text-xs text-amber-600 mt-2">Os seus dados serao pre-preenchidos a partir do cartao anterior se o numero for reconhecido.</p>
                </div>
              )}

              {issueType === '' && (
                <p className="text-xs text-red-500 mt-2">Seleccione o tipo de pedido para continuar.</p>
              )}
            </div>

            {user && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                <UserCheck size={18} className="text-green-600" />
                <span className="text-sm text-green-800">Os seus dados foram preenchidos automaticamente a partir do seu perfil.</span>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className={labelCls}>Nome Completo *</label>
                <input className={inputCls} value={form.full_name} onChange={set('full_name')} placeholder="Nome completo conforme passaporte" data-testid="input-full-name" />
              </div>
              <div>
                <label className={labelCls}>Data de Nascimento *</label>
                <input type="date" className={inputCls} value={form.birth_date} onChange={set('birth_date')} data-testid="input-birth-date" />
              </div>
              <div>
                <label className={labelCls}>Naturalidade *</label>
                <input className={inputCls} value={form.birth_place} onChange={set('birth_place')} placeholder="Local de nascimento" data-testid="input-birth-place" />
              </div>
              <div>
                <label className={labelCls}>Municipio *</label>
                <input className={inputCls} value={form.municipality} onChange={set('municipality')} placeholder="Municipio de origem" data-testid="input-municipality" />
              </div>
              <div>
                <label className={labelCls}>Provincia *</label>
                <select className={inputCls} value={form.province} onChange={set('province')} data-testid="select-province">
                  <option value="">Selecione a provincia</option>
                  {ANGOLA_PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Habilitacoes Literarias</label>
                <select className={inputCls} value={form.education} onChange={set('education')} data-testid="select-education">
                  <option value="">Selecione</option>
                  {EDUCATION_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Profissao</label>
                <input className={inputCls} value={form.profession} onChange={set('profession')} placeholder="Profissao" />
              </div>
              <div>
                <label className={labelCls}>Estado Civil</label>
                <select className={inputCls} value={form.marital_status} onChange={set('marital_status')}>
                  <option value="">Selecione</option>
                  {MARITAL_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Local de Trabalho ou Estudos</label>
                <input className={inputCls} value={form.workplace} onChange={set('workplace')} placeholder="Local de trabalho" />
              </div>
              <div>
                <label className={labelCls}>Contacto Telefonico *</label>
                <input className={inputCls} value={form.phone} onChange={set('phone')} placeholder="+34 ..." data-testid="input-phone" />
              </div>
              <div>
                <label className={labelCls}>Email *</label>
                <input type="email" className={inputCls} value={form.email} onChange={set('email')} placeholder="email@exemplo.com" data-testid="input-email" />
              </div>
              <div>
                <label className={labelCls}>N. Passaporte *</label>
                <input className={inputCls} value={form.passport_number} onChange={set('passport_number')} placeholder="N. Passaporte" data-testid="input-passport" />
              </div>
              <div>
                <label className={labelCls}>Nacionalidade</label>
                <input className={inputCls} value={form.nationality} onChange={set('nationality')} />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Residencia Actual / Endereco em Espanha *</label>
                <input className={inputCls} value={form.address_spain} onChange={set('address_spain')} placeholder="Morada completa" data-testid="input-address" />
              </div>
              <div>
                <label className={labelCls}>Cidade *</label>
                <input className={inputCls} value={form.city} onChange={set('city')} placeholder="Madrid" data-testid="input-city" />
              </div>
              <div>
                <label className={labelCls}>Codigo Postal</label>
                <input className={inputCls} value={form.postal_code} onChange={set('postal_code')} placeholder="28001" />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Filiacao */}
        {step === 2 && (
          <div className="bg-white rounded-xl border p-6 space-y-5" data-testid="step-filiation">
            <div className="flex items-center gap-3 mb-2">
              <Users size={24} className="text-[#CC0000]" />
              <div>
                <h2 className="text-lg font-bold text-gray-900">Filiacao</h2>
                <p className="text-xs text-gray-500">Dados dos pais</p>
              </div>
            </div>

            <div className="p-5 bg-blue-50 rounded-xl border border-blue-100">
              <h3 className="font-semibold text-blue-800 text-sm mb-4">Dados do Pai</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className={labelCls}>Nome do Pai *</label>
                  <input className={inputCls} value={form.father_name} onChange={set('father_name')} placeholder="Nome completo" data-testid="input-father-name" />
                </div>
                <div>
                  <label className={labelCls}>Data de Nascimento</label>
                  <input type="date" className={inputCls} value={form.father_birth_date} onChange={set('father_birth_date')} />
                </div>
                <div className="md:col-span-3">
                  <label className={labelCls}>Local de Residencia</label>
                  <input className={inputCls} value={form.father_residence} onChange={set('father_residence')} placeholder="Residencia" />
                </div>
              </div>
            </div>

            <div className="p-5 bg-pink-50 rounded-xl border border-pink-100">
              <h3 className="font-semibold text-pink-800 text-sm mb-4">Dados da Mae</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className={labelCls}>Nome da Mae *</label>
                  <input className={inputCls} value={form.mother_name} onChange={set('mother_name')} placeholder="Nome completo" data-testid="input-mother-name" />
                </div>
                <div>
                  <label className={labelCls}>Data de Nascimento</label>
                  <input type="date" className={inputCls} value={form.mother_birth_date} onChange={set('mother_birth_date')} />
                </div>
                <div className="md:col-span-3">
                  <label className={labelCls}>Local de Residencia</label>
                  <input className={inputCls} value={form.mother_residence} onChange={set('mother_residence')} placeholder="Residencia" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Trajectoria e Filhos */}
        {step === 3 && (
          <div className="bg-white rounded-xl border p-6 space-y-6" data-testid="step-trajectory">
            <div className="flex items-center gap-3 mb-2">
              <MapPin size={24} className="text-[#CC0000]" />
              <div>
                <h2 className="text-lg font-bold text-gray-900">Trajectoria</h2>
                <p className="text-xs text-gray-500">Informacao sobre a sua chegada a Espanha</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Data de Chegada a Espanha</label>
                <input type="date" className={inputCls} value={form.arrival_date_spain} onChange={set('arrival_date_spain')} />
              </div>
              <div>
                <label className={labelCls}>Motivo da Viagem a Espanha</label>
                <input className={inputCls} value={form.travel_reason} onChange={set('travel_reason')} placeholder="Ex: Trabalho, Estudos..." />
              </div>
            </div>

            <hr />

            <div>
              <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-4"><Baby size={20} className="text-[#CC0000]" /> Filhos (maximo 7)</h3>
              <p className="text-xs text-gray-500 mb-4">Preencha apenas as linhas correspondentes aos filhos que pretende registar.</p>
              <div className="space-y-2">
                {children.map((child, idx) => (
                  <div key={idx} className="grid grid-cols-[2rem_1fr_11rem] items-center gap-3 py-2 border-b border-gray-100">
                    <span className="text-sm font-bold text-gray-400">{idx + 1}-</span>
                    <input className={inputCls} value={child.name} onChange={setChild(idx, 'name')} placeholder="Nome do filho" data-testid={`child-name-${idx}`} />
                    <input type="date" className={inputCls} value={child.birth_date} onChange={setChild(idx, 'birth_date')} data-testid={`child-date-${idx}`} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Documentos */}
        {step === 4 && (
          <div className="bg-white rounded-xl border p-6 space-y-5" data-testid="step-documents">
            <div className="flex items-center gap-3 mb-2">
              <Upload size={24} className="text-[#CC0000]" />
              <div>
                <h2 className="text-lg font-bold text-gray-900">Documentos</h2>
                <p className="text-xs text-gray-500">Fotografia e documentos de suporte</p>
              </div>
            </div>

            {/* Photo section */}
            <div className="p-5 bg-amber-50 rounded-xl border-2 border-amber-300">
              <div className="flex items-center gap-2 mb-3">
                <Camera size={20} className="text-amber-700" />
                <h3 className="font-bold text-amber-800">Fotografia Tipo Passe *</h3>
                <span className="ml-auto text-xs font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">Obrigatoria</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4 text-xs text-amber-700">
                <div className="flex items-center gap-1"><CheckCircle size={12} /> Fundo branco ou claro</div>
                <div className="flex items-center gap-1"><CheckCircle size={12} /> Rosto visivel e centrado</div>
                <div className="flex items-center gap-1"><CheckCircle size={12} /> Formato JPG ou PNG</div>
                <div className="flex items-center gap-1"><CheckCircle size={12} /> Max 5MB</div>
              </div>
              <div className="flex items-start gap-5">
                {photoPreview ? (
                  <div className="relative">
                    <img src={photoPreview} alt="Foto" className="w-28 h-36 object-cover rounded-lg border-2 border-amber-400 shadow" />
                    <button onClick={() => { setPhotoFile(null); setPhotoPreview(null); }} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600">
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <div className="w-28 h-36 bg-white rounded-lg border-2 border-dashed border-amber-300 flex flex-col items-center justify-center text-amber-300 gap-1">
                    <User size={32} />
                    <span className="text-xs text-amber-400">Sem foto</span>
                  </div>
                )}
                <div className="flex-1">
                  <label className="cursor-pointer flex flex-col items-center justify-center gap-2 px-4 py-6 border-2 border-dashed border-amber-300 rounded-xl bg-white text-amber-700 hover:border-amber-500 hover:bg-amber-50 transition-all">
                    <Upload size={24} />
                    <span className="font-semibold text-sm">{photoFile ? 'Alterar fotografia' : 'Clique para carregar fotografia'}</span>
                    <input type="file" accept="image/jpeg,image/png,image/jpg" onChange={handlePhoto} className="hidden" data-testid="input-photo" />
                  </label>
                  {photoFile && photoValidation.error && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-xs text-red-600">
                      <AlertCircle size={14} /> {photoValidation.error}
                    </div>
                  )}
                  {photoFile && !photoValidation.error && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-xs text-green-600">
                      <CheckCircle size={14} /> Fotografia valida - {photoFile.name} ({(photoFile.size / 1024).toFixed(0)}KB)
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Categorized documents */}
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-800 text-sm">Documentos de Identificacao</h3>
              {DOC_CATEGORIES.map(cat => (
                <div key={cat.id} className={`p-4 rounded-xl border ${docsByCategory[cat.id] ? 'border-green-300 bg-green-50' : cat.required ? 'border-gray-200 bg-white hover:border-[#CC0000]' : 'border-gray-200 bg-gray-50 hover:border-gray-300'} transition-all`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <cat.icon size={16} className="text-gray-500" />
                      <span className="font-semibold text-sm text-gray-800">{cat.label}</span>
                      {cat.required && <span className="text-xs text-red-500 font-bold">*</span>}
                    </div>
                    {docsByCategory[cat.id] && (
                      <div className="flex items-center gap-2 text-xs text-green-600">
                        <CheckCircle size={14} />
                        <span>{docsByCategory[cat.id].name}</span>
                        <button onClick={() => setDocsByCategory(p => ({ ...p, [cat.id]: null }))} className="text-red-400 hover:text-red-600 ml-1">
                          <X size={12} />
                        </button>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mb-3">{cat.sublabel}</p>
                  {!docsByCategory[cat.id] && (
                    <label className="cursor-pointer flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs text-gray-600 hover:border-[#CC0000] hover:text-[#CC0000] w-fit transition-all">
                      <Upload size={14} /> Carregar ficheiro (JPG, PNG ou PDF)
                      <input type="file" accept="image/jpeg,image/png,application/pdf" onChange={handleDocUpload(cat.id)} className="hidden" data-testid={`input-doc-${cat.id}`} />
                    </label>
                  )}
                </div>
              ))}
            </div>

            {/* Other documents */}
            <div className="p-4 rounded-xl border border-dashed border-gray-300 bg-gray-50">
              <h3 className="font-semibold text-sm text-gray-700 mb-1 flex items-center gap-2"><Upload size={16} /> Outros Documentos (opcional)</h3>
              <p className="text-xs text-gray-500 mb-3">Documentos adicionais relevantes (ex: carta de justificacao, certidoes, etc.)</p>
              <label className="cursor-pointer flex items-center justify-center gap-2 py-5 border-2 border-dashed border-gray-300 rounded-xl text-gray-400 hover:border-[#CC0000] hover:text-[#CC0000] transition-all">
                <input type="file" multiple accept="image/*,application/pdf" onChange={handleOtherDocs} className="hidden" data-testid="input-other-docs" />
                <Upload size={20} /> Carregar documentos adicionais
              </label>
              {docsByCategory.other.length > 0 && (
                <div className="mt-3 space-y-2">
                  {docsByCategory.other.map((f, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 bg-white rounded-lg border text-sm">
                      <span className="flex items-center gap-2 text-gray-600"><FileText size={14} className="text-gray-400" /> {f.name}</span>
                      <button onClick={() => setDocsByCategory(p => ({ ...p, other: p.other.filter((_, j) => j !== i) }))} className="text-red-400 hover:text-red-600 text-xs">Remover</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-2 text-xs text-blue-700">
              <Shield size={14} className="mt-0.5 shrink-0" />
              <span>Os seus documentos sao transmitidos de forma segura e encriptada. So sao acedidos pelos funcionarios consulares responsaveis pelo seu pedido, em conformidade com o RGPD.</span>
            </div>

            <div>
              <label className={labelCls}>Observacoes</label>
              <textarea className={inputCls} rows={3} value={form.notes} onChange={set('notes')} placeholder="Notas adicionais (opcional)" />
            </div>
          </div>
        )}

        {/* Step 5: Confirmacao */}
        {step === 5 && (
          <div className="bg-white rounded-xl border p-6 space-y-5" data-testid="step-confirmation">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle size={24} className="text-green-600" />
              <div>
                <h2 className="text-lg font-bold text-gray-900">Confirmacao dos Dados</h2>
                <p className="text-xs text-gray-500">Verifique os dados antes de prosseguir</p>
              </div>
            </div>

            {issueType === 'renewal' && form.previous_card_number && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                Renovacao do cartao <strong>{form.previous_card_number}</strong>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SummaryBlock title="Identificacao" items={[
                ['Tipo', issueType === 'renewal' ? 'Renovacao' : 'Nova Emissao'],
                ['Nome', form.full_name], ['Nascimento', form.birth_date], ['Naturalidade', form.birth_place],
                ['Municipio', form.municipality], ['Provincia', form.province],
                ['Passaporte', form.passport_number], ['Telefone', form.phone], ['Email', form.email],
              ]} />
              <SummaryBlock title="Morada em Espanha" items={[
                ['Endereco', form.address_spain], ['Cidade', form.city], ['Codigo Postal', form.postal_code],
                ['Profissao', form.profession], ['Habilitacoes', form.education],
              ]} />
              <SummaryBlock title="Filiacao" items={[
                ['Pai', form.father_name], ['Mae', form.mother_name],
              ]} />
              <SummaryBlock title="Documentos" items={[
                ['Foto', photoFile ? photoFile.name : 'Nao carregada'],
                ['Passaporte', docsByCategory.passport?.name || '-'],
                ['Residencia', docsByCategory.residence?.name || '-'],
                ['Filhos', `${children.filter(c => c.name.trim()).length} registado(s)`],
              ]} />
            </div>

            <div className="p-5 bg-gradient-to-r from-[#CC0000]/10 to-[#FFD700]/10 rounded-xl border border-[#CC0000]/20 text-center">
              <p className="text-lg font-bold text-[#CC0000]">Taxa de Inscricao Consular: EUR 5,00</p>
              <p className="text-sm text-gray-600 mt-1">Apos confirmar, sera solicitada a sua assinatura digital</p>
            </div>
          </div>
        )}

        {/* Step 6: Assinatura e RGPD */}
        {step === 6 && (
          <div className="bg-white rounded-xl border p-6 space-y-6" data-testid="step-signature">
            <div className="flex items-center gap-3 mb-2">
              <PenTool size={24} className="text-[#CC0000]" />
              <div>
                <h2 className="text-lg font-bold text-gray-900">Assinatura e Consentimento RGPD</h2>
                <p className="text-xs text-gray-500">Por favor assine o formulario e aceite os termos</p>
              </div>
            </div>

            <div className="p-5 bg-gray-50 rounded-xl border">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-800 text-sm">Assinatura do Titular *</h3>
                <button onClick={clearSignature} className="px-3 py-1 text-xs text-red-600 hover:bg-red-50 rounded-lg font-medium">Limpar</button>
              </div>
              <div className="border-2 border-gray-300 rounded-lg bg-white overflow-hidden">
                <canvas ref={canvasRef} width={600} height={200} className="w-full cursor-crosshair touch-none"
                  onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing} />
              </div>
              <p className="text-xs text-gray-500 mt-2 text-center">Utilize o rato ou o dedo para assinar</p>
              {signatureData && (
                <div className="mt-2 flex items-center justify-center gap-2 text-green-600 text-sm">
                  <CheckCircle size={16} /><span>Assinatura capturada</span>
                </div>
              )}
            </div>

            <div className="p-5 bg-blue-50 rounded-xl border border-blue-200">
              <div className="flex items-start gap-3 mb-4">
                <Shield size={24} className="text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-blue-900 text-sm">Regulamento Geral de Protecao de Dados (RGPD)</h3>
                  <p className="text-xs text-blue-700 mt-1">Conforme a legislacao espanhola e europeia vigente</p>
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 text-xs text-gray-600 space-y-3 max-h-48 overflow-y-auto border border-blue-100">
                <p><strong>INFORMACAO SOBRE PROTECAO DE DADOS PESSOAIS</strong></p>
                <p>Em conformidade com o Regulamento (UE) 2016/679 do Parlamento Europeu e do Conselho, de 27 de abril de 2016, relativo a protecao das pessoas singulares no que diz respeito ao tratamento de dados pessoais (RGPD), informamos que:</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li><strong>Responsavel pelo tratamento:</strong> Embaixada de Angola em Espanha, Calle Lagasca 88, 2. Andar, 28001 Madrid.</li>
                  <li><strong>Finalidade:</strong> Os seus dados serao tratados para a gestao do pedido de Cartao Consular, registo consular e prestacao de servicos consulares.</li>
                  <li><strong>Legitimacao:</strong> O tratamento e necessario para o cumprimento de uma obrigacao legal e o exercicio de funcoes consulares.</li>
                  <li><strong>Destinatarios:</strong> Os dados poderao ser comunicados as autoridades angolanas competentes para efeitos de registo consular.</li>
                  <li><strong>Conservacao:</strong> Os dados serao conservados durante o periodo necessario para cumprir as finalidades indicadas e as obrigacoes legais aplicaveis.</li>
                  <li><strong>Direitos:</strong> Pode exercer os seus direitos de acesso, retificacao, supressao, limitacao, portabilidade e oposicao atraves do email: protecaodados@embajadangola.es</li>
                </ul>
              </div>
              <label className="flex items-start gap-3 mt-4 cursor-pointer">
                <input type="checkbox" checked={rgpdAccepted} onChange={(e) => setRgpdAccepted(e.target.checked)} className="mt-1 w-5 h-5 rounded border-blue-300 text-[#CC0000] focus:ring-[#CC0000]" data-testid="rgpd-checkbox" />
                <span className="text-sm text-gray-700">
                  <strong>Declaro que li e aceito</strong> os termos de protecao de dados pessoais acima descritos e autorizo o tratamento dos meus dados para as finalidades indicadas, em conformidade com o RGPD e a Lei Organica 3/2018 de Protecao de Dados Pessoais de Espanha.
                </span>
              </label>
            </div>

            {(!signatureData || !rgpdAccepted) && (
              <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <AlertCircle size={18} className="text-amber-600" />
                <span className="text-sm text-amber-700">Por favor assine o formulario e aceite os termos RGPD para continuar</span>
              </div>
            )}
          </div>
        )}

        {/* Step 7: Pagamento */}
        {step === 7 && (
          <div className="bg-white rounded-xl border p-10 text-center" data-testid="step-payment">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CreditCard size={40} className="text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Pedido Registado com Sucesso!</h2>
            {cardResult && (
              <p className="text-lg text-gray-600 mb-2">
                Referencia: <span className="font-bold text-[#CC0000]">{cardResult.reference_code}</span>
              </p>
            )}
            <p className="text-gray-500 text-sm mb-8 max-w-md mx-auto">
              O seu pedido foi registado. Para concluir o processo, efectue o pagamento da taxa de EUR 5,00.
            </p>
            <div className="p-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border mb-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-600">Taxa de Inscricao Consular</span>
                <span className="text-2xl font-bold text-[#CC0000]">EUR 5,00</span>
              </div>
              <button onClick={proceedToPayment} disabled={submitting}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#CC0000] text-white rounded-xl text-lg font-medium hover:bg-[#A30000] transition-colors disabled:opacity-50" data-testid="btn-pay">
                {submitting ? <><Loader2 size={20} className="animate-spin" /> A processar...</> : <><CreditCard size={20} /> Proceder ao Pagamento</>}
              </button>
            </div>
            <p className="text-xs text-gray-400">Pagamento seguro processado via Stripe.</p>
          </div>
        )}

        {/* Step 8: Payment Success */}
        {step === 8 && (
          <div className="bg-white rounded-xl border p-10 text-center" data-testid="step-success">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Pagamento Confirmado!</h2>
            {cardResult && (
              <p className="text-lg text-gray-600 mb-2">
                Referencia: <span className="font-bold text-[#CC0000]">{cardResult.reference_code}</span>
              </p>
            )}
            <p className="text-gray-500 text-sm mb-8 max-w-md mx-auto">
              O seu pagamento foi processado com sucesso. Recebera um email com a fatura.
              O seu Cartao Consular sera enviado por email apos aprovacao pelo Agente Consular.
            </p>
            {cardResult && cardResult.payment_status === 'paid' && (
              <div className="mb-8 p-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border">
                <h3 className="font-bold text-gray-800 text-lg mb-4">Documentos Disponiveis</h3>
                <div className="flex flex-col sm:flex-row justify-center gap-3">
                  <button onClick={downloadInvoice} className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-700 text-white rounded-lg text-sm font-medium hover:bg-gray-800" data-testid="btn-download-invoice">
                    <FileText size={18} /> Descarregar Fatura
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-4">O Cartao Consular sera enviado automaticamente por email apos aprovacao.</p>
              </div>
            )}
            <div className="flex justify-center gap-4">
              <Link to="/" className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">Voltar ao Inicio</Link>
              <Link to="/servicos-consulares" className="px-6 py-3 bg-[#CC0000] text-white rounded-lg text-sm font-medium hover:bg-[#A30000]">Ver Outros Servicos</Link>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        {step >= 1 && step <= 6 && (
          <div className="flex justify-between mt-6">
            <button onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-40" data-testid="btn-prev">
              {t('common.previous')}
            </button>
            {step < 6 ? (
              <button onClick={() => setStep(step + 1)} disabled={!canNext()}
                className="px-8 py-3 bg-[#CC0000] text-white rounded-lg text-sm font-medium hover:bg-[#A30000] disabled:opacity-40" data-testid="btn-next">
                {t('common.next')}
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={submitting || !canNext()}
                className="px-8 py-3 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-40" data-testid="btn-submit">
                {submitting ? 'A processar...' : 'Submeter e Proceder ao Pagamento'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryBlock({ title, items }) {
  return (
    <div className="p-4 bg-gray-50 rounded-xl border">
      <h3 className="font-semibold text-gray-800 text-sm mb-3 pb-2 border-b">{title}</h3>
      <div className="space-y-2">
        {items.filter(([, v]) => v).map(([label, value]) => (
          <div key={label} className="flex justify-between text-xs">
            <span className="text-gray-500">{label}</span>
            <span className="text-gray-800 font-medium text-right max-w-[60%] truncate">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
